// Sandbox tenants + application certification (Agent Platform Phase 9).
// Both are platform-operator surfaces on auth-service via the gateway.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);

export interface SandboxTenant {
  id: string;
  name: string;
  slug: string;
  adminEmail: string;
  isActive: boolean;
  users: number;
  createdAt: string;
}

export const listSandboxTenants = async (): Promise<SandboxTenant[]> => {
  const { data } = await axios.get(`${API_V1}/platform/sandbox-tenants`);
  return data.sandboxes || [];
};

export const createSandboxTenant = async (input: {
  name: string;
  adminEmail: string;
  adminName?: string;
  adminPassword: string;
}): Promise<{ id: string; slug: string; name: string; adminEmail: string }> => {
  const { data } = await axios.post(`${API_V1}/platform/sandbox-tenants`, input);
  return data.sandbox;
};

export interface CertificationApp {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  clientId: string;
  kind: string;
  environment: string;
  certificationStatus: string;
  certificationEvidence?: {
    ranAt?: string;
    passed?: boolean;
    checks?: Record<string, { passed: boolean; statusCode?: number; detail?: string; error?: string }>;
  } | null;
  certificationRequestedAt?: string;
  certifiedAt?: string;
  certificationNote?: string;
}

export const listCertifications = async (status?: string): Promise<CertificationApp[]> => {
  const { data } = await axios.get(`${API_V1}/platform/certifications`, { params: { status } });
  return data.applications || [];
};

export const decideCertification = async (
  id: string,
  decision: 'certified' | 'rejected',
  note?: string,
): Promise<void> => {
  await axios.post(`${API_V1}/platform/certifications/${id}/decide`, { decision, note });
};

// ---- Platform AI clients (universal-LLM Phase 3) ---------------------------

export interface PlatformAiClient {
  id: string;
  name: string;
  clientId: string;
  kind: string;
  source: string;
  platformLevel: boolean;
  confidential: boolean;
  status: string;
  scopes: string[];
  environment: string;
  certificationStatus?: string;
  createdAt: string;
  lastUsedAt?: string | null;
  activeConnections: number;
  tenantsLinked: number;
}

export const listAiClients = async (): Promise<PlatformAiClient[]> => {
  const { data } = await axios.get(`${API_V1}/platform/ai-clients`);
  return data.clients || [];
};

export const disableAiClient = async (id: string): Promise<{ revokedTokens: number }> => {
  const { data } = await axios.post(`${API_V1}/platform/ai-clients/${id}/disable`);
  return data;
};

export const enableAiClient = async (id: string): Promise<void> => {
  await axios.post(`${API_V1}/platform/ai-clients/${id}/enable`);
};

export const purgeAiClientOrphans = async (): Promise<{ purged: number; kept: number }> => {
  const { data } = await axios.post(`${API_V1}/platform/ai-clients/purge-orphans`, {});
  return data;
};
